/** @jsxImportSource @emotion/react */
import React from "react";
import * as Icons from "@goodgym/icons";
import * as UI from "@goodgym/components";
import * as T from "@goodgym/graphql/types";
import * as u from "@goodgym/util";
import _ from "lodash";
import { useDropdown } from "@goodgym/hooks";
import { Link as RouterLink } from "react-router-dom";

export type NotificationsProps = {};

const groupNotificationsByCheeredObject = (notifications: T.Notification[]) => {
  const grouped = _.groupBy(
    notifications,
    (n) => `${n.cheerType}-${n.cheeredId}`
  );

  return Object.entries(grouped).map(([key, group]) => {
    const first = group[0];
    const userNames = group.map((n) => n.notifierName).filter(Boolean);

    return {
      key: key,
      cheerType: first.cheerType,
      cheeredId: first.cheeredId,
      users: userNames,
      imageUrl: first.imageUrl,
      createdAt: first.createdAt,
      read: group.every((n) => n.read),
      url: first.url,
    };
  });
};

const NotificationContent: React.FC<{
  users: string[];
  imageUrl?: string | null;
  createdAt: string;
  cheerType?: string | null;
  subdued?: boolean;
}> = ({ users, imageUrl, createdAt, cheerType, subdued = false }) => {
  const [first, second, third, ...others] = users;
  const base = [first, second, third].filter(Boolean).join(", ");
  const remainingCount = others.length;

  return (
    <>
      <UI.ListItemIcon sx={{ pr: 2 }}>
        <UI.Avatar src={imageUrl ?? undefined} />
      </UI.ListItemIcon>
      <UI.Box>
        <UI.Typography>
          <>
            {base}
            {remainingCount > 0 && (
              <>
                {" and "}
                <UI.Tooltip title={others.join(", ")} placement="top" arrow>
                  <span
                    style={{
                      textDecoration: "underline",
                      cursor: "pointer",
                    }}
                  >
                    {remainingCount} others
                  </span>
                </UI.Tooltip>
              </>
            )}
            {" cheered your "}
            {cheerType?.toLowerCase()}
          </>
        </UI.Typography>
        <UI.Typography color={subdued ? "neutral.main" : undefined}>
          {u.time.formatDateTime(createdAt)}
        </UI.Typography>
      </UI.Box>
    </>
  );
};

const Notifications: React.FC<NotificationsProps> = ({}) => {
  const { open, onClick, onClose, anchor } = useDropdown();

  const { data } = T.useNotificationsQuery();
  const [markAllAsRead] = T.useMarkAllNotificationsAsReadMutation({
    refetchQueries: [T.NotificationsDocument],
    awaitRefetchQueries: true,
  });

  const notifications = data?.runner?.notifications || [];

  const groupedNotifications = groupNotificationsByCheeredObject(notifications);

  const unread = groupedNotifications.filter((n) => !n.read);
  const read = groupedNotifications.filter((n) => n.read);

  return (
    <>
      <UI.NavbarButton
        id="notifications-button"
        icon={Icons.Notifications}
        label="Notifications"
        onClick={onClick}
        aria-controls={open ? "notifications-menu" : undefined}
        aria-haspopup="true"
        aria-expanded={open ? "true" : undefined}
        counter={unread.length}
      />
      <UI.Menu
        id="notifications-menu"
        anchorEl={anchor}
        open={open}
        onClose={onClose}
        MenuListProps={{
          "aria-labelledby": "notifications-button",
        }}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >
        {unread.map((notification) => {
          const isWorkout = notification.cheerType === "Workout";
          const isAward = notification.cheerType === "Award";
          const toUrl = isWorkout
            ? `/v3#workout${notification.cheeredId}`
            : isAward
            ? `/v3#award${notification.cheeredId}`
            : notification.url;

          return isWorkout ? (
            <UI.MenuItem
              key={`${notification.cheerType}-unread-${notification.cheeredId}`}
              component={RouterLink}
              to={toUrl}
              onClick={onClose}
            >
              <NotificationContent
                users={notification.users.filter((u): u is string =>
                  Boolean(u)
                )}
                imageUrl={notification.imageUrl}
                createdAt={notification.createdAt}
                cheerType={notification.cheerType}
                subdued
              />
            </UI.MenuItem>
          ) : (
            <UI.MenuItem
              key={`${notification.cheerType}-unread-${notification.cheeredId}`}
              component="a"
              href={toUrl}

              onClick={onClose}
            >
              <NotificationContent
                users={notification.users.filter((u): u is string =>
                  Boolean(u)
                )}
                imageUrl={notification.imageUrl}
                createdAt={notification.createdAt}
                cheerType={notification.cheerType}
                subdued
              />
            </UI.MenuItem>
          );
        })}

        {read.map((notification) => {
          const isWorkout = notification.cheerType === "Workout";
          const isAward = notification.cheerType === "Award";
          const toUrl = isWorkout
            ? `/v3#workout${notification.cheeredId}`
            : isAward
            ? `/v3#award${notification.cheeredId}`
            : notification.url;

          return isWorkout ? (
            <UI.MenuItem
              key={`read-${notification.cheerType}-${notification.cheeredId}`}
              component={RouterLink}
              to={toUrl}
              onClick={onClose}
            >
              <NotificationContent
                users={notification.users.filter((u): u is string =>
                  Boolean(u)
                )}
                imageUrl={notification.imageUrl}
                createdAt={notification.createdAt}
                cheerType={notification.cheerType}
                subdued
              />
            </UI.MenuItem>
          ) : (
            <UI.MenuItem
              key={`read-${notification.cheerType}-${notification.cheeredId}`}
              component="a"
              href={toUrl}
              onClick={onClose}
            >
              <NotificationContent
                users={notification.users.filter((u): u is string =>
                  Boolean(u)
                )}
                imageUrl={notification.imageUrl}
                createdAt={notification.createdAt}
                cheerType={notification.cheerType}
                subdued
              />
            </UI.MenuItem>
          );
        })}

        <UI.MenuItem>
          <UI.Button
            variant="text"
            onClick={() => markAllAsRead()}
            fullWidth
            disabled={unread.length === 0}
          >
            Mark all as read
          </UI.Button>
        </UI.MenuItem>
      </UI.Menu>
    </>
  );
};

export default Notifications;
